/** Articles du devis — + devant le libellé ouvre le catalogue, sans décaler. */
import { useState } from "react";
import { FxSelect } from "../../components/FxSelect";
import { RichText, richOrPlain } from "../../components/RichText";
import { useFormatters } from "../../formats/useFormatters";
import { parseQty, typedDecimal, type Product, type TaxRate } from "../catalog/types";
import { QuoteCatalog } from "./QuoteCatalog";
import { lineTotals } from "./format";
import { emptyLine, lineFromProduct, lineKeptWithoutLabel, type LineDraft } from "./types";
import { opNatureOf, opNaturePrint } from "../../legal/opNature";
import "./quotes.css";

type QuoteLinesProps = {
  companyId: number;
  lines: LineDraft[];
  rates: TaxRate[];
  decimalComma: boolean;
  locked: boolean;
  defaultTaxId: number | null;
  onChange: (lines: LineDraft[]) => void;
};

export function QuoteLines({
  companyId,
  lines,
  rates,
  decimalComma,
  locked,
  defaultTaxId,
  onChange,
}: QuoteLinesProps) {
  const { money } = useFormatters();
  const [pickAt, setPickAt] = useState<number | null>(null);

  function set(i: number, patch: Partial<LineDraft>) {
    onChange(lines.map((l, n) => (n === i ? { ...l, ...patch } : l)));
  }

  function applyProduct(p: Product) {
    if (pickAt === null) return;
    const next = lineFromProduct(p, decimalComma);
    onChange(lines.map((l, n) => (n === pickAt ? { ...next, key: l.key } : l)));
  }

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= lines.length) return;
    const next = lines.slice();
    const swap = next[i];
    next[i] = next[j];
    next[j] = swap;
    onChange(next);
    if (pickAt === i) setPickAt(j);
    else if (pickAt === j) setPickAt(i);
  }

  return (
    <section className="fx-quote-work">
      {pickAt !== null && !locked ? (
        <QuoteCatalog companyId={companyId} onPick={applyProduct} onClose={() => setPickAt(null)} />
      ) : null}
      <div className="fx-quote-sheet">
        <div className="fx-quote-sheet__bar">
          <p>
            Articles
            <span className="fx-quote-nature">{opNaturePrint(opNatureOf(lines))}</span>
          </p>
          {!locked ? (
            <div className="fx-quote-sheet__acts">
              <button
                type="button"
                className="fx-quote-chip"
                onClick={() => onChange([...lines, emptyLine(defaultTaxId, decimalComma)])}
              >
                + Article
              </button>
            </div>
          ) : null}
        </div>
        <div className="fx-quote-sheet__scroll">
          <table className="fx-quote-lines">
            <thead>
              <tr>
                <th className="is-label">Désignation</th>
                <th className="is-kind">Nature</th>
                <th className="is-desc">Description</th>
                <th className="is-num">Prix unitaire</th>
                <th className="is-qty">Qté</th>
                <th className="is-tax">TVA</th>
                <th className="is-off">Remise</th>
                <th className="is-num">Total</th>
                {!locked ? <th className="is-rm" /> : null}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const t = lineTotals(l, rates, decimalComma);
                return (
                  <tr key={l.key}>
                    <td className="is-label" data-label="Désignation">
                      {locked ? (
                        <strong>{l.label}</strong>
                      ) : (
                        <div className="fx-quote-desig">
                          <button
                            type="button"
                            className="fx-quote-plus"
                            aria-label="Choisir un produit"
                            onClick={() => setPickAt(i)}
                          >
                            +
                          </button>
                          <input
                            className={`fx-q-ctrl is-compact${lineKeptWithoutLabel(l, decimalComma) ? " is-miss" : ""}`}
                            value={l.label}
                            placeholder="Libellé"
                            aria-invalid={lineKeptWithoutLabel(l, decimalComma)}
                            onChange={(e) => set(i, { label: e.target.value })}
                          />
                        </div>
                      )}
                    </td>
                    <td className="is-kind" data-label="Nature">
                      {locked ? (
                        <span className="fx-quote-ro">{l.op_kind === "good" ? "Biens" : "Services"}</span>
                      ) : (
                        <FxSelect
                          compact
                          value={l.op_kind}
                          options={[
                            { value: "service", label: "Services" },
                            { value: "good", label: "Biens" },
                          ]}
                          onChange={(v) => set(i, { op_kind: v === "good" ? "good" : "service" })}
                        />
                      )}
                    </td>
                    <td className="is-desc" data-label="Description">
                      {locked ? (
                        l.description.trim() ? (
                          <span className="fx-quote-ro fx-quote-ro-desc" dangerouslySetInnerHTML={{ __html: richOrPlain(l.description) }} />
                        ) : (
                          <span className="fx-quote-ro">—</span>
                        )
                      ) : (
                        <RichText
                          compact
                          fonts={false}
                          label="Descriptif"
                          resetKey={l.key}
                          value={richOrPlain(l.description)}
                          onChange={(html) => set(i, { description: html })}
                        />
                      )}
                    </td>
                    <td className="is-num" data-label="Prix unitaire">
                      {locked ? (
                        money(t.gross !== null ? Math.round(t.gross / Math.max(parseQty(l.quantity) || 1, 0.001)) : 0)
                      ) : (
                        <input
                          className="fx-q-ctrl is-compact is-end"
                          value={l.price}
                          inputMode="decimal"
                          onChange={(e) => set(i, { price: typedDecimal(e.target.value, decimalComma) })}
                        />
                      )}
                    </td>
                    <td className="is-qty" data-label="Qté">
                      {locked ? (
                        l.quantity
                      ) : (
                        <input
                          className="fx-q-ctrl is-compact is-end"
                          value={l.quantity}
                          inputMode="decimal"
                          onChange={(e) => set(i, { quantity: typedDecimal(e.target.value, decimalComma) })}
                        />
                      )}
                    </td>
                    <td className="is-tax" data-label="TVA">
                      {locked ? (
                        rates.find((r) => r.id === Number(l.tax_rate_id))?.label || "—"
                      ) : (
                        <FxSelect
                          compact
                          value={l.tax_rate_id == null ? "" : String(l.tax_rate_id)}
                          placeholder="—"
                          options={rates.map((r) => ({ value: String(r.id), label: r.label }))}
                          onChange={(v) => set(i, { tax_rate_id: v === "" ? null : Number(v) })}
                        />
                      )}
                    </td>
                    <td className="is-off" data-label="Remise">
                      {locked ? (
                        l.discount ? `${l.discount} ${l.discount_kind === "eur" ? "€" : "%"}` : "—"
                      ) : (
                        <div className="fx-q-unit is-compact">
                          <input
                            className="fx-q-ctrl is-compact is-end"
                            value={l.discount}
                            inputMode="decimal"
                            placeholder="0"
                            onChange={(e) => set(i, { discount: typedDecimal(e.target.value, decimalComma) })}
                          />
                          <FxSelect
                            compact
                            plain
                            value={l.discount_kind}
                            options={[
                              { value: "pct", label: "%" },
                              { value: "eur", label: "€" },
                            ]}
                            onChange={(v) => set(i, { discount_kind: v === "eur" ? "eur" : "pct" })}
                          />
                        </div>
                      )}
                    </td>
                    <td className="is-num is-sum" data-label="Total">{t.ht === null ? "—" : money(t.ht)}</td>
                    {!locked ? (
                      <td className="is-rm" data-label="">
                        <div className="fx-quote-row-acts">
                          <button
                            type="button"
                            className="fx-quote-icon"
                            aria-label="Monter la ligne"
                            disabled={i === 0}
                            onClick={() => move(i, -1)}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="fx-quote-icon"
                            aria-label="Descendre la ligne"
                            disabled={i === lines.length - 1}
                            onClick={() => move(i, 1)}
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            className="fx-quote-icon is-danger"
                            aria-label="Retirer la ligne"
                            onClick={() => onChange(lines.filter((_, n) => n !== i))}
                          >
                            ×
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
